<script lang="ts">
	import { SVG } from '@wiseed/wikit';
	import debounce from 'lodash-es/debounce';
	import { isEmpty } from '@wiseed/wikit';
	import type { InputRangeProps } from '@wiseed/wikit';
	import { InputRange } from '@wiseed/wikit';
	import { investAmountRule } from '@wiseed/wikit';
	import { t } from 'svelte-i18n';

	export let props: InputRangeProps;

	props = {
		thumbColor: 'white',
		thumbWidth: 20,
		trackColor: 'grey-dark',
		trackHeight: 2,
		...props,
		validation: {
			rules: [
				investAmountRule(
					props.min,
					props.max,
					$t('slideInvest.betweenValues', { values: { min: props.min, max: props.max } })
				)
			]
		}
	};

	const handleRemove = () => {
		if (props.step && props.value - props.step >= props.min) {
			props.value = parseFloat((props.value - props.step).toFixed(2));
		} else if (props.value - 1 >= props.min) {
			props.value -= 1;
		} else {
			props.value = props.min;
		}
	};

	const handleAdd = () => {
		if (props.step && props.value + props.step <= props.max) {
			props.value = parseFloat((props.value + props.step).toFixed(2));
		} else if (props.value + 1 <= props.max) {
			props.value += 1;
		} else {
			props.value = props.max;
		}
	};

	const findClosestFromStep = (v: number | string): number => {
		if (!isEmpty(v as string)) {
			if (v > props.max) {
				return props.max;
			} else if (v < props.min) {
				return props.min;
			} else {
				let r = v % props.step;
				if (r > 0) {
					if (r < props.step / 2) {
						return v - r;
					} else {
						return v - r + props.step;
					}
				}
			}
			v = typeof v === 'string' ? parseInt(v) : v;
			return v;
		}

		return null;
	};

	const isOutOfRange = (v: number) => v < props.min || v > props.max;

	const onKeyUp = debounce((e) => {
		const targetValue = e.target.value;
		if (!isNaN(targetValue)) {
			let closestValue = findClosestFromStep(targetValue);
			if (closestValue) {
				props.value = closestValue;
			}
		}
	}, 500);

	$: if (props.value < 0) props.value = props.min;

	const handleFirstFocus = () => {
		if (!props.touched) {
			props.touched = true;
		} else if (!props.touchedTwice) {
			props.touchedTwice = true;
		}
	};

	const handleFirstChange = () => {
		if (!props.dirty) {
			props.dirty = true;
		}
	};
</script>

<div>
	<div class="flex justify-between items-center">
		<button
			type="button"
			class="border border-grey-light rounded p-1 flex-shrink-0"
			on:click={() => handleRemove()}
			disabled={props.disabled}
		>
			<SVG name="remove" />
		</button>
		<div class="flex-1">
			<input
				type="number"
				step="any"
				on:keyup={onKeyUp}
				bind:value={props.value}
				class:out-of-range={isOutOfRange(props.value)}
				on:focus|once={() => handleFirstFocus()}
				on:input|once={() => handleFirstChange()}
				disabled={props.disabled}
			/>
		</div>
		<button
			type="button"
			class="border border-grey-light rounded p-1 flex-shrink-0"
			on:click={() => handleAdd()}
			disabled={props.disabled}
		>
			<SVG name="add" />
		</button>
	</div>
	<InputRange bind:props />
	<div class="pt-2 text-sm text-center">
		{$t('operation.performance.intentionHelp', { values: { inputStep: props.step } })}
	</div>
</div>

<style lang="postcss">
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	input[type='number'] {
		-moz-appearance: textfield;
	}

	input {
		filter: none;
		@apply bg-transparent text-center text-5xl font-bold border-0 m-0 w-full;
		@apply focus:outline-none focus:ring-0;
	}

	input.out-of-range {
		@apply text-danger;
	}
	button[disabled],
	input[disabled] {
		@apply cursor-not-allowed;
	}
</style>
